import React, { useEffect, useMemo, useState } from 'react';
import {
  FileSignature,
  Download,
  FileBadge,
  RefreshCw,
  Search,
  MapPin,
  X,
  ClipboardList,
  Wand2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import requests from '../../../services/httpServices';
import { downloadContractCertificate } from '../../../services/contractAuditService';

const formatStamp = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const locationText = (geo) => {
  if (!geo) return '';
  return [geo.city, geo.region, geo.country].filter(Boolean).join(', ');
};

const shortenUa = (ua) => {
  if (!ua) return '';
  const os =
    /Windows NT 10/i.test(ua) ? 'Windows' :
    /Mac OS X/i.test(ua) ? 'macOS' :
    /Linux/i.test(ua) ? 'Linux' :
    /Android/i.test(ua) ? 'Android' :
    /iPhone|iPad/i.test(ua) ? 'iOS' : '';
  const browser =
    /Edg\//i.test(ua) ? 'Edge' :
    /Chrome\//i.test(ua) ? 'Chrome' :
    /Firefox\//i.test(ua) ? 'Firefox' :
    /Safari\//i.test(ua) ? 'Safari' : '';
  if (browser && os) return `${browser} on ${os}`;
  return browser || os || ua.slice(0, 40);
};

function FieldsModal({ submission, onClose }) {
  if (!submission) return null;
  const fields = submission.filledFields || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative w-full max-w-2xl max-h-[80vh] bg-[#0D1117] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-purple-400" />
              Filled Fields
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {submission.participantName || 'Participant'}
              {submission.participantEmail
                ? ` (${submission.participantEmail})`
                : ''}
              {' — '}
              {formatStamp(submission.createdAt)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {fields.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">
              No filled fields recorded for this submission.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fields.map((f, idx) => {
                const isSig =
                  f.type === 'signature' || f.type === 'initials';
                const isCheck = f.type === 'checkbox';
                return (
                  <div
                    key={f.fieldId || idx}
                    className="rounded-xl border border-white/10 bg-white/5 px-4 py-3"
                  >
                    <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">
                      {f.label || f.type}
                      <span className="ml-1.5 px-1.5 py-0.5 rounded bg-white/5 text-gray-600 normal-case">
                        {f.type}
                      </span>
                    </div>
                    {isSig ? (
                      f.value && f.value.startsWith('data:image') ? (
                        <img
                          src={f.value}
                          alt={f.label || f.type}
                          className="max-h-20 max-w-full object-contain bg-white rounded-lg p-2"
                        />
                      ) : (
                        <span className="text-gray-400 text-sm italic">
                          {f.value === '[signature-image]'
                            ? 'Signature captured (see PDF)'
                            : f.value || '—'}
                        </span>
                      )
                    ) : isCheck ? (
                      <span
                        className={`text-sm font-medium ${
                          f.value === 'true'
                            ? 'text-green-400'
                            : 'text-gray-500'
                        }`}
                      >
                        {f.value === 'true' ? 'Checked' : 'Unchecked'}
                      </span>
                    ) : (
                      <div className="text-white text-sm break-words">
                        {f.value || '—'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SignedContractsPage() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [modalSubmission, setModalSubmission] = useState(null);
  const [regenerating, setRegenerating] = useState(new Set());
  const [bulkRegenerating, setBulkRegenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await requests.get('/contract/my-submissions?limit=300');
      if (res?.success) {
        setSubmissions(res.submissions || []);
      } else {
        setError(res?.message || 'Failed to load submissions');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const regenerateOne = async (submissionId) => {
    setRegenerating((prev) => new Set(prev).add(submissionId));
    try {
      const res = await requests.post(
        `/contract/submission/${encodeURIComponent(submissionId)}/regenerate`
      );
      if (res?.success) {
        toast.success('PDF regenerated');
        setSubmissions((prev) =>
          prev.map((s) =>
            (s._id || s.id) === submissionId
              ? { ...s, submissionUrl: res.submissionUrl }
              : s
          )
        );
      } else {
        toast.error(res?.message || 'Regeneration failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Regeneration failed');
    } finally {
      setRegenerating((prev) => {
        const next = new Set(prev);
        next.delete(submissionId);
        return next;
      });
    }
  };

  const regenerateAll = async () => {
    if (!window.confirm('Regenerate PDFs for ALL signed contracts? This may take a while.')) return;
    setBulkRegenerating(true);
    try {
      const res = await requests.post('/contract/submissions/regenerate-all');
      if (res?.success) {
        toast.success(
          `Regenerated ${res.processed} of ${res.total} PDFs` +
            (res.failed ? ` (${res.failed} failed)` : '')
        );
        await load();
      } else {
        toast.error(res?.message || 'Bulk regeneration failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Bulk regeneration failed');
    } finally {
      setBulkRegenerating(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return submissions;
    return submissions.filter((s) => {
      const hay = [
        s.participantName,
        s.participantEmail,
        s.meetingId,
        s.contractElementId,
        s.ipAddress,
        locationText(s.geoLocation),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [query, submissions]);

  const buildDownloadUrl = (s) => {
    const base = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
    const qs = new URLSearchParams({
      url: s.submissionUrl || '',
      name: `signed-${s.participantName || 'contract'}.pdf`,
      meetingId: s.meetingId || '',
      actorName: s.participantName || 'Host',
      actorRole: 'host',
    }).toString();
    return `${base}/contract/audit/${encodeURIComponent(
      s.contractElementId
    )}/download?${qs}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
            <FileSignature className="w-6 h-6 text-purple-400" />
            Signed Contracts
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Every contract signed inside an R-Link meeting, with its audit
            trail and Certificate of Completion.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500"
              size={14}
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, meeting, IP…"
              className="bg-white/5 border border-white/10 rounded-md pl-8 pr-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-purple-500 w-64"
            />
          </div>
          <button
            type="button"
            onClick={load}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-white text-sm hover:bg-white/10"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          <button
            type="button"
            onClick={regenerateAll}
            disabled={bulkRegenerating}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-purple-600/20 border border-purple-500/30 text-purple-300 text-sm hover:bg-purple-600/30 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Regenerate all filled PDFs using the latest layout"
          >
            <Wand2 size={14} />
            {bulkRegenerating ? 'Regenerating…' : 'Regenerate All'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-4 py-3">
          {error}
        </div>
      )}

      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-gray-400 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left font-medium px-4 py-3">Signer</th>
                <th className="text-left font-medium px-4 py-3">Meeting</th>
                <th className="text-left font-medium px-4 py-3">Signed</th>
                <th className="text-left font-medium px-4 py-3">Location</th>
                <th className="text-left font-medium px-4 py-3">Device</th>
                <th className="text-right font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center text-gray-400 py-8">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-gray-500 py-10">
                    {submissions.length === 0
                      ? 'No signed contracts yet.'
                      : 'No matches for your search.'}
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((s) => {
                  const rowId = s._id || s.id;
                  const loc = locationText(s.geoLocation);
                  const fields = s.filledFields || [];
                  const hasFields = fields.length > 0;
                  return (
                    <tr key={rowId} className="hover:bg-white/5">
                      <td className="px-4 py-3 align-top">
                        <div className="text-white font-medium">
                          {s.participantName || 'Participant'}
                        </div>
                        {s.participantEmail && (
                          <div className="text-gray-500 text-xs">
                            {s.participantEmail}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="text-gray-300 text-xs font-mono">
                          {s.meetingId || '—'}
                        </div>
                        <div className="text-gray-600 text-[10px] font-mono mt-0.5">
                          {s.contractElementId?.slice(-8)}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top text-gray-300 whitespace-nowrap">
                        {formatStamp(s.createdAt)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {loc ? (
                          <div className="flex items-start gap-1 text-gray-300">
                            <MapPin
                              size={12}
                              className="mt-0.5 text-gray-500 flex-shrink-0"
                            />
                            <span>{loc}</span>
                          </div>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                        {s.ipAddress && (
                          <div className="text-gray-600 text-[10px] font-mono mt-0.5">
                            {s.ipAddress}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-gray-400 text-xs">
                        {shortenUa(s.userAgent)}
                      </td>
                      <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-2">
                          {hasFields && (
                            <button
                              type="button"
                              onClick={() => setModalSubmission(s)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white/5 border border-white/10 text-gray-200 text-xs hover:bg-white/10"
                              title="View filled fields"
                            >
                              <ClipboardList size={12} />
                              Fields ({fields.length})
                            </button>
                          )}
                          {s.submissionUrl && (
                            <a
                              href={buildDownloadUrl(s)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white/5 border border-white/10 text-gray-200 text-xs hover:bg-white/10"
                              title="Download signed PDF"
                            >
                              <Download size={12} />
                              PDF
                            </a>
                          )}
                          {hasFields && s.originalDocumentUrl && (
                            <button
                              type="button"
                              onClick={() => regenerateOne(rowId)}
                              disabled={regenerating.has(rowId)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-purple-600/20 border border-purple-500/30 text-purple-300 text-xs hover:bg-purple-600/30 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Regenerate this PDF from filled fields"
                            >
                              <Wand2 size={12} />
                              {regenerating.has(rowId) ? '...' : 'Regen'}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              downloadContractCertificate({
                                contractElementId: s.contractElementId,
                                contractName: 'Contract',
                                participantId: s.participantId,
                                participantName: s.participantName,
                              })
                            }
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-purple-600/20 border border-purple-500/30 text-purple-300 text-xs hover:bg-purple-600/30"
                            title={`Certificate for ${s.participantName || 'this signer'}`}
                          >
                            <FileBadge size={12} />
                            Certificate
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fields Modal */}
      {modalSubmission && (
        <FieldsModal
          submission={modalSubmission}
          onClose={() => setModalSubmission(null)}
        />
      )}
    </div>
  );
}
